import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, Globe, ChevronDown, Layers, Trash2, ChevronRight, Phone } from "lucide-react";
import { CONFIGURATOR_TABS } from "./ConfiguratorTabs";
import { useI18n } from "../contexts/I18nContext";
import { useCart } from "../contexts/CartContext";
import { useComposition } from "../contexts/CompositionContext";
import { findProduct } from "../data/products";
import SiteSearch from "./SiteSearch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./ui/dropdown-menu";

const LOGO =
  "https://customer-assets.emergentagent.com/job_246bdc4f-04a8-499f-a35e-a2d34f82e829/artifacts/etd6qiby_46448324-8a4b-431b-a4f6-49b603bebb50.png";

export default function Header() {
  const { t, lang, setLang, languages } = useI18n();
  const { totalItems, setIsOpen } = useCart();
  const { summary, reset: resetComposition, setPreviousSlug } = useComposition();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compositionOpen, setCompositionOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Slug actuel (si on est sur une page produit) — utilisé pour mémoriser
  // d'où l'utilisateur vient lorsqu'il ouvre un autre produit depuis le menu.
  const currentSlug = (() => {
    const m = location.pathname.match(/^\/produits\/[^/]+\/([^/]+)/);
    return m ? m[1] : null;
  })();

  const PRODUCT_LABEL_FR = {
    "croix-classique-led": "Croix classique",
    "croix-neon-led": "Croix néon LED",
    "lettre-boitier-alu": "Lettres boîtier",
    "lettre-retro-eclaire": "Lettres rétro",
    "lettre-inox-3d": "Lettres inox 3D",
    "enseigne-boitier-chassis": "Enseigne châssis",
    "enseigne-interieur-neon-led": "Néon intérieur",
  };

  const handleClearComposition = () => {
    resetComposition();
    setCompositionOpen(false);
  };

  const navLinks = [
    { to: "/", label: t.nav.home },
    { to: "/produits", label: t.nav.products },
    { to: "/galerie", label: t.nav.gallery },
    { to: "/faq", label: t.nav.faq },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/75 border-b border-white/5"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="header-logo-link"
          onClick={() => setMobileOpen(false)}
        >
          <img
            src={LOGO}
            alt="Signapharma"
            className="h-12 w-auto transition-all"
          />
          <span className="hidden sm:block font-display font-bold tracking-wider text-lg uppercase chrome-text">
            Signapharma
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => {
            // Le lien PRODUITS reçoit un mega-menu (hover desktop) listant
            // les 7 configurateurs avec leur icône. Sur mobile, la nav mobile
            // existante reste un simple lien.
            if (link.to === "/produits") {
              return <ProductsMegaMenu key={link.to} link={link} lang={lang} location={location} />;
            }
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `font-display font-semibold uppercase tracking-[0.15em] text-sm px-4 py-2 transition-colors ${
                    isActive
                      ? "text-[#39FF14] neon-text-soft"
                      : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {/* Téléphone — cliquable pour appel automatique */}
          <a
            href="tel:+33610443864"
            aria-label="Appeler Signapharma"
            title="+33 6 10 44 38 64"
            className="hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black font-mono text-xs uppercase tracking-widest transition-colors whitespace-nowrap"
            data-testid="header-phone-link"
          >
            <Phone size={14} />
            <span>+33 6 10 44 38 64</span>
          </a>
          {/* Version mobile compacte : icône seule */}
          <a
            href="tel:+33610443864"
            aria-label="Appeler Signapharma"
            className="sm:hidden p-2 border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors"
            data-testid="header-phone-link-mobile"
          >
            <Phone size={16} />
          </a>
          {/* Search */}
          <SiteSearch />
          {/* Language */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-switcher-trigger"
              className="flex items-center gap-1.5 px-3 py-2 font-mono text-xs uppercase tracking-widest text-zinc-300 hover:text-[#39FF14] transition-colors border border-transparent hover:border-[#39FF14]/30"
            >
              <Globe size={14} />
              <span>{lang}</span>
              <ChevronDown size={12} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-none min-w-[160px]"
            >
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`font-mono text-xs uppercase tracking-widest cursor-pointer focus:bg-[#39FF14]/10 focus:text-[#39FF14] ${
                    lang === l.code ? "text-[#39FF14]" : "text-zinc-300"
                  }`}
                >
                  <span className="mr-2">{l.code.toUpperCase()}</span>
                  <span className="text-zinc-500">{l.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Composition pill — visible dès qu'au moins 1 élément est configuré */}
          {summary.total > 0 && (
            <DropdownMenu open={compositionOpen} onOpenChange={setCompositionOpen}>
              <DropdownMenuTrigger
                data-testid="composition-pill"
                className="hidden sm:flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-[#39FF14]/40 bg-[#39FF14]/[0.06] text-[#39FF14] hover:bg-[#39FF14]/[0.12] transition-colors"
              >
                <Layers size={14} />
                <span>{lang === "fr" ? "Mon montage" : lang === "de" ? "Meine Komposition" : lang === "it" ? "La mia composizione" : "My composition"}</span>
                <span className="font-bold">{summary.total}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#0a0a0a] border border-[#1f1f1f] rounded-none min-w-[280px] p-3"
                data-testid="composition-dropdown"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                  {lang === "fr" ? "Éléments configurés" : "Configured items"}
                </div>
                <div className="space-y-1.5 mb-3">
                  {summary.slugs.map(({ slug, count }) => (
                    <button
                      key={slug}
                      type="button"
                      onClick={() => {
                        setCompositionOpen(false);
                        // Mémorise la page courante pour afficher un bandeau
                        // "Retour à …" sur la page de destination.
                        if (currentSlug && currentSlug !== slug) {
                          setPreviousSlug(currentSlug);
                        }
                        const product = findProduct(slug);
                        const cat = product?.category || "croix-pharmacie";
                        navigate(`/produits/${cat}/${slug}`);
                      }}
                      data-testid={`composition-item-${slug}`}
                      className="w-full flex items-center justify-between px-3 py-2 border border-white/10 hover:border-[#39FF14]/50 hover:bg-[#39FF14]/[0.05] transition-colors text-left"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-200">
                        {PRODUCT_LABEL_FR[slug] || slug}
                      </span>
                      <span className="font-mono text-[10px] text-[#39FF14]">×{count}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleClearComposition}
                  data-testid="composition-clear"
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={12} />
                  {lang === "fr" ? "Vider mon montage" : "Clear composition"}
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Cart */}
          <button
            onClick={() => setIsOpen(true)}
            data-testid="header-cart-button"
            className="relative p-2 text-zinc-300 hover:text-[#39FF14] transition-colors"
            aria-label={t.nav.cart}
          >
            <ShoppingCart size={20} />
            {totalItems > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-1 -right-1 bg-[#39FF14] text-black text-[10px] font-bold font-mono w-5 h-5 flex items-center justify-center"
              >
                {totalItems}
              </span>
            )}
          </button>

          {/* Voir le panier CTA desktop (remplace ancien "Demander un devis") */}
          <button
            onClick={() => navigate("/panier")}
            data-testid="header-view-cart-cta"
            className="hidden md:inline-flex btn-neon text-xs"
          >
            {lang === "fr" ? "Voir le panier" : "View cart"}
          </button>

          {/* Mobile burger */}
          <button
            className="lg:hidden p-2 text-zinc-300 hover:text-[#39FF14] transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={t.common.menu}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-[#050505] border-t border-white/5"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `font-display font-semibold uppercase tracking-[0.15em] text-sm py-3 border-b border-white/5 ${
                    isActive ? "text-[#39FF14]" : "text-zinc-300"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setMobileOpen(false);
                navigate("/panier");
              }}
              className="btn-neon mt-4 justify-center"
              data-testid="mobile-view-cart-cta"
            >
              {lang === "fr" ? "Voir le panier" : "View cart"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}


// =============================================================================
// ProductsMegaMenu — hover dropdown qui liste les 7 configurateurs avec icône
// et description courte. Affiché à la place du simple NavLink "PRODUITS"
// sur desktop. Sur mobile, la navigation reste un lien simple.
// =============================================================================
function ProductsMegaMenu({ link, lang, location }) {
  const [open, setOpen] = useState(false);
  const isActive = location.pathname.startsWith("/produits");
  const SHORT_DESC = {
    "croix-classique-led": {
      fr: "LED basse conso, designs classiques", de: "LED, klassische Designs",
      en: "Energy-efficient LED, classic designs", it: "LED, design classici"
    },
    "croix-neon-led": {
      fr: "Néon LED haute brillance, traits personnalisables",
      de: "Neon LED, anpassbare Striche", en: "High-bright neon LED, customizable strokes",
      it: "Neon LED, tratti personalizzabili"
    },
    "croix-point-a-point": {
      fr: "Croix LED animée point par point, messages défilants",
      de: "Animiertes Punkt-zu-Punkt-LED-Kreuz", en: "Dot-matrix animated LED cross",
      it: "Croce LED animata punto a punto"
    },
    "lettre-boitier-alu": {
      fr: "Lettres boîtier alu, faces acrylique LED",
      de: "Alu-Buchstaben mit LED", en: "Aluminum box letters, LED-lit",
      it: "Lettere scatolate alluminio LED"
    },
    "lettre-retro-eclaire": {
      fr: "Halo lumineux subtil, finition premium",
      de: "Sanftes Halo, Premium-Finish", en: "Subtle halo glow, premium finish",
      it: "Alone luminoso premium"
    },
    "lettre-inox-3d": {
      fr: "Inox poli/brossé, finitions or & cuivre",
      de: "Edelstahl poliert/gebürstet", en: "Stainless steel polished/brushed",
      it: "Inox lucido/spazzolato"
    },
    "enseigne-boitier-chassis": {
      fr: "Châssis alu profilé, mise en valeur de l'enseigne",
      de: "Alu-Rahmen", en: "Aluminum frame, sign showcase",
      it: "Telaio alluminio"
    },
    "enseigne-interieur-neon-led": {
      fr: "Néon flexible LED pour vitrine intérieure",
      de: "LED-Flex für Innenvitrinen", en: "Flexible LED neon for indoor windows",
      it: "Neon LED flessibile vetrina interna"
    },
  };
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="products-megamenu"
    >
      <Link
        to="/produits"
        data-testid="nav-produits"
        className={`inline-flex items-center gap-1 font-display font-semibold uppercase tracking-[0.15em] text-sm px-4 py-2 transition-colors ${
          isActive ? "text-[#39FF14] neon-text-soft" : "text-zinc-300 hover:text-white"
        }`}
      >
        {link.label}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>
      {open && (
        <div
          className="absolute top-full left-0 w-[640px] -translate-x-1/4 mt-1 bg-[#0a0a0a]/98 backdrop-blur-xl border border-[#1f1f1f] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.7)]"
          data-testid="products-megamenu-panel"
          style={{ zIndex: 60 }}
        >
          <div className="p-5 grid grid-cols-2 gap-1.5">
            {CONFIGURATOR_TABS.map((tab) => {
              const Icon = tab.icon;
              const label = tab.label[lang] || tab.label.fr;
              const desc = (SHORT_DESC[tab.slug] || {})[lang] || (SHORT_DESC[tab.slug] || {}).fr || "";
              return (
                <Link
                  key={tab.slug}
                  to={`/produits/${tab.cat}/${tab.slug}`}
                  data-testid={`megamenu-${tab.slug}`}
                  onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 p-3 border border-transparent hover:border-[#39FF14]/40 hover:bg-[#39FF14]/[0.04] transition-colors"
                >
                  <span className="shrink-0 w-9 h-9 inline-flex items-center justify-center border border-white/10 bg-black/40 group-hover:border-[#39FF14]/60 group-hover:bg-[#39FF14]/10 transition-colors">
                    <Icon size={16} className="text-[#39FF14]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display font-semibold text-xs uppercase tracking-[0.12em] text-zinc-100 group-hover:text-[#39FF14] transition-colors">
                      {label}
                    </span>
                    <span className="block mt-0.5 font-mono text-[10px] text-zinc-500 leading-snug truncate">{desc}</span>
                  </span>
                  <ChevronRight size={14} className="shrink-0 text-zinc-600 group-hover:text-[#39FF14] transition-colors" />
                </Link>
              );
            })}
          </div>
          <Link
            to="/enseigne-pharmacie-france"
            onClick={() => setOpen(false)}
            data-testid="megamenu-seo-france"
            className="block px-5 py-3 border-t border-[#39FF14]/20 bg-[#39FF14]/[0.06] hover:bg-[#39FF14]/[0.12] transition-colors group"
          >
            <span className="flex items-center justify-between gap-3">
              <span className="min-w-0">
                <span className="block font-display font-semibold text-xs uppercase tracking-[0.12em] text-[#39FF14] neon-text-soft">
                  {lang === "fr" ? "Enseignes pharmacie France" : "Pharmacy signs France"}
                </span>
                <span className="block mt-0.5 font-mono text-[10px] text-zinc-400 truncate">
                  {lang === "fr"
                    ? "Étude et installation partout en France"
                    : "Study and installation across France"}
                </span>
              </span>
              <ChevronRight size={14} className="shrink-0 text-[#39FF14]" />
            </span>
          </Link>
          <Link
            to="/produits"
            onClick={() => setOpen(false)}
            data-testid="megamenu-view-all"
            className="block px-5 py-3 border-t border-white/5 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] hover:bg-[#39FF14]/[0.04] transition-colors"
          >
            {lang === "fr" ? "Voir tous les produits" : "View all products"}
          </Link>
        </div>
      )}
    </div>
  );
}
